import { XMarkIcon } from '@heroicons/react/20/solid';
import { ReactNode } from 'react';

type ColorOption = 'gray' | 'red' | 'yellow' | 'green' | 'blue' | 'violet' | 'indigo' | 'pink' | 'orange';

const styles = {
    sizes: {
        small: 'px-2 py-0.5 text-xs',
        large: 'px-2 py-0.5 text-sm',
    },
    iconColors: {
        gray: 'text-gray-600 dark:text-gray-400',
        red: 'text-red-600 dark:text-red-400',
        yellow: 'text-yellow-600 dark:text-yellow-400',
        green: 'text-green-600 dark:text-green-400',
        blue: 'text-blue-600 dark:text-blue-400',
        violet: 'text-violet-600 dark:text-violet-400',
        indigo: 'text-indigo-600 dark:text-indigo-400',
        pink: 'text-pink-600 dark:text-pink-400',
        orange: 'text-orange-600 dark:text-orange-400',
    },
    colors: {
        gray: 'border border-gray-200 bg-gray-50 text-gray-900 dark:border-gray-400 dark:bg-gray-400/10 dark:text-gray-400',
        red: 'border border-red-200 bg-red-50 text-gray-900 dark:border-red-400 dark:bg-red-400/10 dark:text-red-400',
        yellow: 'border border-yellow-200 bg-yellow-50 text-gray-900 dark:border-yellow-400 dark:bg-yellow-400/10 dark:text-yellow-400',
        green: 'border border-green-200 bg-green-50 text-gray-900 dark:border-green-400 dark:bg-green-400/10 dark:text-green-400',
        blue: 'border border-blue-200 bg-blue-50 text-gray-900 dark:border-blue-400 dark:bg-blue-400/10 dark:text-blue-400',
        violet: 'border border-violet-200 bg-violet-50 text-gray-900 dark:border-violet-400 dark:bg-violet-400/10 dark:text-violet-400',
        indigo: 'border border-indigo-200 bg-indigo-50 text-gray-900 dark:border-indigo-400 dark:bg-indigo-400/10 dark:text-indigo-400',
        pink: 'border border-pink-200 bg-pink-50 text-gray-900 dark:border-pink-400 dark:bg-pink-400/10 dark:text-pink-400',
        orange: 'border border-orange-200 bg-orange-50 text-gray-900 dark:border-orange-400 dark:bg-orange-400/10 dark:text-orange-400',
    },
};

export type BadgeV3Props = {
    size?: 'small' | 'large';
    color?: ColorOption;
    rounded?: boolean;
    avatar?: ReactNode;
    leftIcon?: ReactNode;
    label?: string;
    focusable?: boolean;
    onRemove?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
};

function BadgeV3({
    size = 'small',
    color = 'gray',
    rounded = false,
    avatar,
    leftIcon,
    label = '',
    focusable = true,
    onRemove,
}: BadgeV3Props): JSX.Element {
    return (
        <div
            className={`font-normal inline-flex items-center ${rounded ? 'rounded-full' : 'rounded-md'} ${styles.sizes[size]} ${
                styles.colors[color]
            }`}
        >
            {leftIcon !== undefined && (
                <span className={`${label ? 'mr-1.5' : ''} ${size === 'small' ? 'h-3.5 w-3.5' : 'h-4 w-4'} ${styles.iconColors[color]}`}>
                    {leftIcon}
                </span>
            )}
            {avatar !== undefined && <span className={`${label ? 'mr-1.5' : ''}`}>{avatar}</span>}
            <div className="break-all">{label}</div>
            {onRemove && (
                <button tabIndex={focusable ? 0 : -1} onClick={onRemove} className="ml-1 -mr-1">
                    <XMarkIcon className={`${size === 'small' ? 'h-3 w-3' : 'h-3.5 w-3.5'}`} />
                </button>
            )}
        </div>
    );
}

export default BadgeV3;
